'use client';

import { AnimatePresence, motion } from 'motion/react';

import { DemoFrame, useDemoLoop } from './_lib/demo';

/**
 * Domain 2 — Ads creation & management. Copy + image + CTA snap into a finished
 * ad, variants fan out, and a live spend/CPL row appears. Conveys: we turn the
 * winning patterns into your ads and manage the spend.
 */
export interface AdsCreationCopy {
  frameLabel: string;
  headline: string;
  cta: string;
  live: string;
  cplLabel: string;
  cplValue: string;
}

const ease = [0.22, 0.68, 0.35, 1] as const;

export function AdsCreationAnimation({ copy }: { copy: AdsCreationCopy }) {
  // steps: 0 parts converge, 1 assembled, 2 variants fan out, 3 metrics
  const { ref, step, fading, inView } = useDemoLoop(4, {
    durations: [1500, 1300, 1200],
    holdMs: 2800,
    offsetMs: 300,
  });
  const assembled = step >= 1;

  return (
    <DemoFrame ref={ref} label={copy.frameLabel} fading={fading}>
      <div className="relative flex h-full items-center justify-center">
        {/* Variants fanned behind */}
        <AnimatePresence>
          {step >= 2 &&
            [-1, 1].map((dir) => (
              <motion.div
                key={dir}
                initial={{ opacity: 0, rotate: 0, x: 0 }}
                animate={{ opacity: 0.5, rotate: dir * 6, x: dir * 26 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease }}
                className="absolute h-[200px] w-[170px] rounded-xl border border-[var(--sel-line)] bg-[var(--sel-bg)]"
              />
            ))}
        </AnimatePresence>

        {/* The composed ad card */}
        <div className="relative z-10 flex h-[200px] w-[170px] flex-col gap-2 rounded-xl border border-[var(--sel-line)] bg-[var(--sel-card)] p-3 shadow-sm">
          <motion.div
            animate={assembled ? { y: 0, opacity: 1 } : { y: -14, opacity: 0 }}
            transition={{ duration: 0.32, ease }}
            className="h-16 rounded-lg bg-[var(--sel-elev)]"
          />
          <motion.div
            animate={assembled ? { x: 0, opacity: 1 } : { x: -14, opacity: 0 }}
            transition={{ duration: 0.32, ease, delay: 0.04 }}
            className="text-sm leading-snug font-semibold text-[var(--sel-ink)]"
          >
            {copy.headline}
          </motion.div>
          <motion.div
            animate={assembled ? { y: 0, opacity: 1 } : { y: 14, opacity: 0 }}
            transition={{ duration: 0.32, ease, delay: 0.08 }}
            className="mt-auto inline-flex h-8 items-center justify-center rounded-md bg-[var(--sel-accent)] text-xs font-medium text-[var(--sel-accent-ink)]"
          >
            {copy.cta}
          </motion.div>
        </div>

        {/* Live management row */}
        <AnimatePresence>
          {step >= 3 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="absolute bottom-0 left-0 flex items-center gap-3 rounded-lg border border-[var(--sel-line)] bg-[var(--sel-card)] px-3 py-2 shadow-sm"
            >
              <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--sel-ink)]">
                <span className="relative flex h-2 w-2">
                  {inView && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--sel-accent)] opacity-60 motion-reduce:animate-none" />
                  )}
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--sel-accent)]" />
                </span>
                {copy.live}
              </span>
              <span className="text-xs text-[var(--sel-muted)]">
                {copy.cplLabel}{' '}
                <span className="sel-mono font-semibold text-[var(--sel-ink)]">
                  {copy.cplValue}
                </span>
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DemoFrame>
  );
}
